import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RINK_LABEL, RINK_BADGE } from "@/lib/rinks";

const fmtDate = (d) => (d && d.includes("-") ? d.split("-").reverse().join("-") : d);
const weekday = (d) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-IE", { weekday: "long" });
  } catch {
    return "";
  }
};

export default function MedicSchedulePage() {
  const { user } = useAuth();
  const isMedic = user?.role === "medic";
  const [params] = useSearchParams();
  const rink = isMedic ? user.rink : (params.get("rink") || "sligo");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/school/medic/schedule", { params: { rink } });
        if (live) setRows(data);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [rink]);

  return (
    <div className="max-w-5xl mx-auto" data-testid="medic-schedule-page">
      <div className="flex items-center gap-3 mb-2">
        <GraduationCap className="w-7 h-7 text-sky-600" />
        <h1 className="font-display text-3xl uppercase text-slate-900">Upcoming School Visits</h1>
      </div>
      <p className="text-sm text-slate-600 mb-5">
        <span className={`badge-brutal ${RINK_BADGE[rink]} mr-2`}>{RINK_LABEL[rink]}</span>
        Read-only schedule of upcoming school bookings — plan cover for busy sessions.
      </p>

      <div className="card-brutal overflow-x-auto p-0">
        <table className="w-full border-collapse" data-testid="medic-schedule-table">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="p-2.5 text-left font-bold">Date</th>
              <th className="p-2.5 text-center font-bold">Time</th>
              <th className="p-2.5 text-left font-bold">School</th>
              <th className="p-2.5 text-center font-bold">Pupils</th>
              <th className="p-2.5 text-left font-bold">Age Group</th>
              <th className="p-2.5 text-left font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="medic-schedule-loading">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="medic-schedule-empty">No upcoming school visits</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={`${r.date}-${r.time}-${r.school_name}`} className="border-t-2 border-slate-200" data-testid={`medic-schedule-row-${r.date}-${r.time}`}>
                <td className="p-2.5 text-sm font-bold">{fmtDate(r.date)} <span className="font-normal text-slate-500">{weekday(r.date)}</span></td>
                <td className="p-2.5 text-sm text-center font-mono-tight">{r.time}</td>
                <td className="p-2.5 text-sm">{r.school_name}</td>
                <td className="p-2.5 text-sm text-center font-bold">{r.group_size}</td>
                <td className="p-2.5 text-sm">{r.age_group}</td>
                <td className="p-2.5 text-sm"><span className="badge-brutal bg-sky-200 text-slate-900">{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
